import { useMutation } from "@tanstack/react-query";
import type { ExternalLinkView, ProjectView } from "@portal/schemas";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
} from "@portal/ui";
import { useState } from "react";
import { addProjectLink, removeProjectLink, updateProjectLink } from "../../api/client";
import { useInvalidate, useProjectLinks, useProjectSuggestions } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { LabelWithHint } from "../../components/Hint";
import { HomeSection } from "../../components/HomeSection";
import { InlineError } from "../../components/InlineError";
import { IconPicker } from "../../components/IconPicker";
import { FAVICON_VALUE, ServiceIcon, resolveIconValue } from "../../components/ServiceIcon";

/**
 * 新しいリンクのアイコンの既定は「サイトのアイコン」(リンク先の favicon。ユーザー要望 2026-10-08)。
 * 取れていないときは、URL・サービスから決めたアイコンを出す。
 */
const DEFAULT_ICON = FAVICON_VALUE;
/** 保存済みのリンクで、アイコンを決められないときの表示用の絵(ユーザー要望 2026-09-16: 「自動」は置かない)。 */
const FALLBACK_ICON = "🔗";

import { Hint } from "../../components/Hint";
import { ExternalLink, MoreHorizontal, Pencil, Plus, Save, Trash2 } from "lucide-react";

/**
 * 並び順(ユーザー決定 2026-09-16: グループ見出しをやめ、アイコンで種別を示す)。
 * 未設定の枠として並ぶサービスを先頭に、それ以外を後ろに置く。
 * サーバー側(packages/domain の DEFAULT_PROJECT_LINK_SERVICES)と同じ順。
 */
const SERVICE_ORDER: ExternalLinkView["service"][] = [
  "BACKLOG_ISSUE",
  "BACKLOG_PROJECT",
  "GOOGLE_CHAT",
  "DRIVE_FOLDER",
  "DRIVE_FILE",
  "GOOGLE_SHEET",
  "KINTONE_APP",
  "KINTONE_RECORD",
  "URL",
];

/** サービス種別の名前。URL も表示名もないリンクの見出しに使う(packages/domain と同じ)。 */
const SERVICE_LABELS: Record<ExternalLinkView["service"], string> = {
  BACKLOG_PROJECT: "Backlog のプロジェクト",
  BACKLOG_ISSUE: "リード課題",
  KINTONE_APP: "kintone のアプリ",
  KINTONE_RECORD: "kintone のレコード",
  DRIVE_FOLDER: "Google Drive のフォルダ",
  DRIVE_FILE: "Google Drive のファイル",
  GOOGLE_SHEET: "スプレッドシート",
  GOOGLE_CHAT: "Google Chat のチャット",
  URL: "リンク",
};

/**
 * 一覧に出す名前。表示名が未設定なら URL のドメイン、URL も未設定ならサービス名を使う
 * (ユーザー要望 2026-09-16)。表示名が URL と同じものは、以前の既定値(URL そのまま)なので
 * 未設定として扱う。サーバー側の判定(packages/domain の linkLabel)と同じ規則。
 */
export function linkLabel(link: Pick<ExternalLinkView, "displayName" | "url" | "service">): string {
  const name = link.displayName.trim();
  if (name !== "" && name !== link.url) return name;
  return link.url === "" ? SERVICE_LABELS[link.service] : hostOf(link.url);
}

/** URL のドメイン。形式が不正なら URL をそのまま返す。 */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** リード管理プロジェクトのキー。サーバー側(packages/domain)と同じ規則。 */
const LEAD_PROJECT_KEY = "LEAD";

/**
 * サービスの固定順で並べる。同じサービスの中は登録順。
 * Backlog の課題は、リード課題(未設定の枠を含む)を先に置く。
 */
export function sortLinks(links: readonly ExternalLinkView[]): ExternalLinkView[] {
  const rank = (l: ExternalLinkView) => {
    const i = SERVICE_ORDER.indexOf(l.service);
    if (i < 0) return SERVICE_ORDER.length;
    if (l.service !== "BACKLOG_ISSUE") return i;
    const lead =
      l.url === "" || (l.externalId ?? "").toUpperCase().startsWith(`${LEAD_PROJECT_KEY}-`);
    return lead ? i : i + 0.5;
  };
  return [...links].sort((a, b) => rank(a) - rank(b));
}

/** 外部サービスへの導線。外部サービスは新規タブで開き、編集 UI は再実装しない(SPEC §14.1)。 */
export function ProjectLinksCard({ project }: { project: ProjectView }) {
  const links = useProjectLinks(project.id);
  const invalidate = useInvalidate();
  const remove = useMutation({
    mutationFn: (linkId: string) => removeProjectLink(project.id, linkId),
    onSuccess: () => invalidate.projects(),
  });
  const [editing, setEditing] = useState<ExternalLinkView | null>(null);
  // アップロードしたアイコン(アイコン帳)も一覧で出せるようにする
  const ordered = sortLinks(links.data?.links ?? []);
  return (
    <HomeSection
      data-testid="project-links-card"
      title="外部リンク"
      description="Backlog / Google Chat / Drive などへの導線"
      action={project.canEdit && <AddLinkDialog project={project} />}
    >
      <div className="flex flex-col gap-4">
        <CardStatus
          isPending={links.isPending}
          isError={links.isError}
          refetch={() => links.refetch()}
        />
        {links.data && links.data.links.length === 0 && (
          <p className="text-sm text-muted-foreground">リンクはありません。</p>
        )}
        {ordered.length > 0 && (
          // ブックマークのカードと同じ見せ方にする(ユーザー要望 2026-09-16):
          // 名前を太字、その下にリンク先を小さく出し、行の区切り線は引かない
          <ul data-testid="project-link-rows" className="flex flex-col text-sm">
            {ordered.map((l) => (
              <li key={l.id} className="flex items-start justify-between gap-2 py-1.5">
                <span className="flex min-w-0 items-start gap-2">
                  <ServiceIcon link={l} className="mt-0.5 size-4.5" />
                  {/* URL 未設定の枠はリンクにしない(ユーザー決定 2026-09-16) */}
                  {l.url === "" ? (
                    <span className="flex min-w-0 flex-col">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate font-medium text-muted-foreground">
                          {linkLabel(l)}
                        </span>
                        <Badge variant="outline">未設定</Badge>
                      </span>
                    </span>
                  ) : (
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block min-w-0 hover:bg-accent"
                    >
                      <span className="inline-flex items-center gap-1.5 font-medium group-hover:underline">
                        {linkLabel(l)}
                        <ExternalLink aria-hidden="true" className="size-3 text-muted-foreground" />
                        {l.status === "BROKEN" && <Badge variant="destructive">リンク切れ</Badge>}
                        {l.status === "INACCESSIBLE" && (
                          <Badge variant="warning">アクセス不可</Badge>
                        )}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {l.url.replace(/^https?:\/\//, "")}
                      </span>
                    </a>
                  )}
                </span>
                {project.canEdit && (
                  // 削除は誤登録の訂正以外では使わないため、メニューの中に置く(フィードバック 2026-09-07)
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`${linkLabel(l)} のメニュー`}
                        disabled={remove.isPending}
                      >
                        <MoreHorizontal />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setEditing(l)}>
                        <Pencil />
                        リンクを編集
                      </DropdownMenuItem>
                      <DropdownMenuItem variant="destructive" onSelect={() => remove.mutate(l.id)}>
                        <Trash2 />
                        リンクを削除
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </li>
            ))}
          </ul>
        )}
        <InlineError error={remove.error} />
        {editing && (
          <EditLinkDialog project={project} link={editing} onClose={() => setEditing(null)} />
        )}
        <BacklogProjectSuggestions project={project} />
      </div>
    </HomeSection>
  );
}

/** Backlog から推定した関連プロジェクト(サジェスト)。一覧の下に出す(ユーザー決定 2026-09-06)。 */
function BacklogProjectSuggestions({ project }: { project: ProjectView }) {
  const suggestions = useProjectSuggestions(project.id);
  const invalidate = useInvalidate();
  const add = useMutation({
    mutationFn: (s: { url: string; name: string }) =>
      addProjectLink(project.id, { url: s.url, displayName: s.name }),
    onSuccess: async () => {
      await Promise.all([invalidate.projects(), suggestions.refetch()]);
    },
  });
  const rows = suggestions.data?.backlogProjects ?? [];
  if (suggestions.isError) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="link-suggestions">
        サジェストを取得できませんでした。
      </p>
    );
  }
  if (suggestions.isPending || rows.length === 0) return null;
  return (
    <section aria-label="サジェスト" data-testid="link-suggestions">
      <h3 className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        サジェスト
        <Hint label="サジェストの説明">
          {suggestions.data?.method === "LLM"
            ? `AI(${suggestions.data.model ?? ""})が Backlog を調べて推定しました。`
            : "Backlog の名前の一致から推定しました。"}
          確認のうえ追加してください。
        </Hint>
      </h3>
      {suggestions.data?.note && (
        <p className="mb-1 text-xs text-muted-foreground">{suggestions.data.note}</p>
      )}
      <ul className="flex flex-col divide-y text-sm">
        {rows.map((s) => (
          <li key={s.key} className="flex items-center justify-between gap-2 py-2">
            <span className="flex min-w-0 flex-col">
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate hover:underline"
              >
                {s.name}
              </a>
              <span className="text-xs text-muted-foreground">{s.reasons.join("、")}</span>
            </span>
            {project.canEdit && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`${s.name} をリンクに追加`}
                title="追加"
                disabled={add.isPending}
                onClick={() => add.mutate({ url: s.url, name: s.name })}
              >
                <Plus />
              </Button>
            )}
          </li>
        ))}
      </ul>
      <InlineError error={add.error} />
    </section>
  );
}

/**
 * リンクの編集(フィードバック 2026-09-07: 登録後に名前を変えたくなる。
 * ユーザー要望 2026-09-16: URL も直せるようにする)。
 */
function EditLinkDialog({
  project,
  link,
  onClose,
}: {
  project: ProjectView;
  link: ExternalLinkView;
  onClose: () => void;
}) {
  const [url, setUrl] = useState(link.url);
  // 「自動」という選択肢は置かず、いま表示されているアイコンを選択済みにする
  // (ユーザー要望 2026-09-16)
  const [icon, setIcon] = useState(() =>
    link.icon === FAVICON_VALUE ? FAVICON_VALUE : resolveIconValue(link) || FALLBACK_ICON,
  );
  // 未設定(空、または以前の既定値である URL そのまま)なら空で開き、既定値はプレースホルダーで示す
  const [displayName, setDisplayName] = useState(
    link.displayName === link.url ? "" : link.displayName,
  );
  const invalidate = useInvalidate();
  const save = useMutation({
    mutationFn: () =>
      updateProjectLink(project.id, link.id, {
        revision: link.revision,
        displayName,
        ...(url.trim() === link.url ? {} : { url }),
        ...(icon === link.icon ? {} : { icon }),
      }),
    onSuccess: async () => {
      await invalidate.projects();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* 長い URL でダイアログからはみ出さないよう min-w-0 を付ける(grid の子は既定で縮まない) */}
      <DialogContent className="min-w-0">
        <form
          className="flex min-w-0 flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader className="min-w-0">
            <DialogTitle>リンクを編集</DialogTitle>
            <DialogDescription className="sr-only">
              表示名・URL・アイコンを変えます。
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-1.5">
            <LabelWithHint
              htmlFor="edit-link-name"
              hint="空にすると、URL のドメイン(URL もなければサービス名)を表示します。"
            >
              表示名
            </LabelWithHint>
            <Input
              id="edit-link-name"
              value={displayName}
              placeholder={url.trim() === "" ? SERVICE_LABELS[link.service] : hostOf(url)}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <LabelWithHint htmlFor="edit-link-url" hint="空にすると「未設定」に戻ります。">
              URL
            </LabelWithHint>
            <Input
              id="edit-link-url"
              type="url"
              value={url}
              onChange={(e) => {
                // URL から決まったアイコンのままなら、新しい URL に合わせて選び直す
                const next = e.target.value;
                const auto = (u: string) =>
                  resolveIconValue({ icon: "", url: u, service: link.service }) || FALLBACK_ICON;
                setIcon((current) => (current === auto(url) ? auto(next) : current));
                setUrl(next);
              }}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor="edit-link-icon">アイコン</Label>
            <IconPicker id="edit-link-icon" value={icon} onChange={setIcon} url={url} />
          </div>
          <InlineError error={save.error} />
          <DialogFooter>
            <Button type="submit" disabled={save.isPending}>
              <Save />
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddLinkDialog({ project }: { project: ProjectView }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [icon, setIcon] = useState(DEFAULT_ICON);
  const invalidate = useInvalidate();
  const add = useMutation({
    mutationFn: () =>
      addProjectLink(project.id, {
        url,
        ...(displayName.trim() ? { displayName } : {}),
        icon,
      }),
    onSuccess: async () => {
      await invalidate.projects();
      setOpen(false);
      setUrl("");
      setDisplayName("");
      setIcon(DEFAULT_ICON);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus />
          追加
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>外部リンクを追加</DialogTitle>
            <DialogDescription className="sr-only">
              表示名・URL・アイコンを入力します。
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <LabelWithHint htmlFor="link-name" hint="省略すると、URL のドメインを表示します。">
              表示名(任意)
            </LabelWithHint>
            <Input
              id="link-name"
              value={displayName}
              placeholder={hostOf(url)}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <LabelWithHint
              htmlFor="link-url"
              hint="URL から、サービスの種類とアイコンを自動で判定します。"
            >
              URL
            </LabelWithHint>
            <Input
              id="link-url"
              type="url"
              placeholder="https://"
              value={url}
              onChange={(e) => {
                // 既定は「サイトのアイコン」なので、URL に合わせてアイコンを選び直さない
                setUrl(e.target.value);
              }}
            />
          </div>
          {/* 新規作成でもアイコンを選べるようにする(ユーザー要望 2026-09-16) */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="link-icon">アイコン</Label>
            <IconPicker id="link-icon" value={icon} onChange={setIcon} url={url} />
          </div>
          <InlineError error={add.error} />
          <DialogFooter>
            <Button type="submit" disabled={!url.trim() || add.isPending}>
              <Plus />
              追加
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
